import type * as maplibregl from 'maplibre-gl'
import type { MapColors } from '@/lib/mapStyle'

type LayerSpecification = maplibregl.LayerSpecification

// Source names
export const SOURCES = {
  LIVE: 'visitors-live',
  ACTIVE: 'visitors-active',
  HOVERED: 'hovered-visitor',
} as const

// Layer names for external reference
export const LAYERS = {
  // Active (non-live) layers
  ACTIVE_CLUSTERS: 'active-clusters',
  ACTIVE_CLUSTER_COUNT: 'active-cluster-count',
  ACTIVE_MARKERS: 'active-markers',
  // Live layers
  LIVE_CLUSTER_PULSE: 'live-cluster-pulse',
  LIVE_CLUSTERS: 'live-clusters',
  LIVE_CLUSTER_COUNT: 'live-cluster-count',
  LIVE_PULSE_RING: 'live-pulse-ring',
  LIVE_MARKERS: 'live-markers',
  // Hovered layers
  HOVERED_PULSE: 'hovered-visitor-pulse',
  HOVERED_MARKER: 'hovered-visitor-marker',
} as const

/** Creates active (non-live) cluster circle layer */
export function createActiveClusterLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.ACTIVE_CLUSTERS,
    type: 'circle',
    source: SOURCES.ACTIVE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': colors.ghostDark,
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        18, // radius for count < 10
        10,
        26, // radius for count >= 10
        30,
        34, // radius for count >= 30
      ],
      'circle-opacity': 0.5,
      'circle-stroke-width': 2,
      'circle-stroke-color': colors.ghostDark,
    },
  }
}

/** Creates active cluster count label layer */
export function createActiveClusterCountLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.ACTIVE_CLUSTER_COUNT,
    type: 'symbol',
    source: SOURCES.ACTIVE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Noto Sans Regular'],
      'text-size': 12,
    },
    paint: {
      'text-color': colors.clusterText,
    },
  }
}

/** Creates active (non-live) individual marker layer */
export function createActiveMarkersLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.ACTIVE_MARKERS,
    type: 'circle',
    source: SOURCES.ACTIVE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'case',
        ['get', 'active'],
        colors.ghost, // Active but not live
        colors.ghostDark, // Inactive
      ],
      'circle-radius': 8,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': [
        'case',
        ['get', 'active'],
        0.85, // Active but not live
        0.6, // Inactive
      ],
    },
  }
}

/** Creates live cluster pulse ring layer (animated) */
export function createLiveClusterPulseLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.LIVE_CLUSTER_PULSE,
    type: 'circle',
    source: SOURCES.LIVE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        20, // base radius for count < 10
        10,
        30, // base radius for count >= 10
        30,
        40, // base radius for count >= 30
      ],
      'circle-opacity': 0.4,
    },
  }
}

/** Creates live cluster circle layer */
export function createLiveClusterLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.LIVE_CLUSTERS,
    type: 'circle',
    source: SOURCES.LIVE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        20, // radius for count < 10
        10,
        30, // radius for count >= 10
        30,
        40, // radius for count >= 30
      ],
      'circle-opacity': 0.85,
      'circle-stroke-width': 2,
      'circle-stroke-color': colors.ghost,
    },
  }
}

/** Creates live cluster count label layer */
export function createLiveClusterCountLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.LIVE_CLUSTER_COUNT,
    type: 'symbol',
    source: SOURCES.LIVE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Noto Sans Regular'],
      'text-size': 12,
    },
    paint: {
      'text-color': colors.clusterText,
    },
  }
}

/** Creates pulse ring layer for live individual markers */
export function createLivePulseRingLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.LIVE_PULSE_RING,
    type: 'circle',
    source: SOURCES.LIVE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': 8,
      'circle-opacity': 0.4,
    },
  }
}

/** Creates live individual marker layer */
export function createLiveMarkersLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.LIVE_MARKERS,
    type: 'circle',
    source: SOURCES.LIVE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': 8,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 1,
    },
  }
}

/** Creates hovered visitor pulse ring layer (hidden by default) */
export function createHoveredPulseLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.HOVERED_PULSE,
    type: 'circle',
    source: SOURCES.HOVERED,
    layout: { visibility: 'none' },
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': 28,
      'circle-opacity': 0.3,
    },
  }
}

/** Creates hovered visitor marker layer (hidden by default) */
export function createHoveredMarkerLayer(colors: MapColors): LayerSpecification {
  return {
    id: LAYERS.HOVERED_MARKER,
    type: 'circle',
    source: SOURCES.HOVERED,
    layout: { visibility: 'none' },
    paint: {
      'circle-color': colors.ghost,
      'circle-radius': 16,
      'circle-stroke-width': 3,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 1,
    },
  }
}

/** Adds all visitor map layers to the map (order matters - later = on top) */
export function addVisitorLayers(map: maplibregl.Map, colors: MapColors) {
  // Active (non-live) layers first (bottom)
  map.addLayer(createActiveClusterLayer(colors))
  map.addLayer(createActiveClusterCountLayer(colors))
  map.addLayer(createActiveMarkersLayer(colors))

  // Live layers on top (with pulse)
  map.addLayer(createLiveClusterPulseLayer(colors))
  map.addLayer(createLiveClusterLayer(colors))
  map.addLayer(createLiveClusterCountLayer(colors))
  map.addLayer(createLivePulseRingLayer(colors))
  map.addLayer(createLiveMarkersLayer(colors))

  // Hovered layers on very top
  map.addLayer(createHoveredPulseLayer(colors))
  map.addLayer(createHoveredMarkerLayer(colors))
}

/** Starts pulse animation for live markers and clusters */
export function startPulseAnimation(map: maplibregl.Map) {
  let pulsePhase = 0

  const animatePulse = () => {
    pulsePhase = (pulsePhase + 0.02) % 1
    const sineValue = Math.sin(pulsePhase * Math.PI * 2)

    // Individual marker pulse
    const markerRadius = 8 + sineValue * 8
    const markerOpacity = 0.4 - sineValue * 0.3

    // Cluster pulse (scale factor applied to base radius)
    const clusterScale = 1 + sineValue * 0.4
    const clusterOpacity = 0.4 - sineValue * 0.3

    try {
      // Animate individual live marker pulse ring
      map.setPaintProperty(LAYERS.LIVE_PULSE_RING, 'circle-radius', markerRadius)
      map.setPaintProperty(LAYERS.LIVE_PULSE_RING, 'circle-opacity', Math.max(0.1, markerOpacity))

      // Animate live cluster pulse ring
      // We need to scale each step value by the cluster scale factor
      map.setPaintProperty(LAYERS.LIVE_CLUSTER_PULSE, 'circle-radius', [
        'step',
        ['get', 'point_count'],
        20 * clusterScale,
        10,
        30 * clusterScale,
        30,
        40 * clusterScale,
      ])
      map.setPaintProperty(LAYERS.LIVE_CLUSTER_PULSE, 'circle-opacity', Math.max(0.1, clusterOpacity))
    } catch {
      // Layers might be removed
      return
    }
    requestAnimationFrame(animatePulse)
  }
  animatePulse()
}
